import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, ReactNode, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { OfferCard, Spacer } from 'components-ui'
import { Hooks, Types } from 'service-api'
import { SearchResultsSkeleton } from './search_result_skeleton'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session'

const SearchPackageResults = ({ offers }: { offers: Types.OfferClientDto[] }) => {
  const { accountName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const [canContinueNavigation, setCanContinueNavigation] = useState(false)
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const selectedOffer = bookingCtx.state.cart.groups?.[0]?.offerSelected?.offer

  useEffect(() => {
    if (!canContinueNavigation) {
      return
    }
    if (!isUserLoggedIn(sessionCtx.state.user)) {
      navigateRoute('loginCheck', { accountName, serviceTypeName })
      return
    }
    if (selectedOffer) {
      navigateRoute('detailsPolicies', { accountName })
    }
  }, [canContinueNavigation, accountName, serviceTypeName, navigateRoute, sessionCtx.state.user, selectedOffer])

  const onSelectOffer = useCallback(
    (offer: Types.OfferClientDto) => {
      bookingCtx.dispatch({
        type: 'SELECT_PACKAGE_OFFER',
        payload: { offer },
      })
      setCanContinueNavigation(true)
    },
    [bookingCtx]
  )

  const renderCard = (offer: Types.OfferClientDto) => {
    const isSelected = selectedOffer?.id === offer.id
    return (
      <Fragment key={offer.id}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
          <OfferCard
            key={offer.id}
            searchResult={{ offer } as Types.BookingSearchClientDto}
            petGroup={0}
            onSelectOffer={(offer, _, __) => onSelectOffer(offer)}
            serviceTypeName="packages"
            specieTypeName=""
            selected={isSelected}
            locationSpecies={bookingCtx.state.config.species}
            temperTest={undefined}
            onSelectScheduleTempTest={() => {}}
          />
        </Paper>
        <Spacer height={3} />
      </Fragment>
    )
  }

  const renderResult = () => {
    const renderItems: ReactNode[] = []

    if (selectedOffer) {
      renderItems.push(renderCard(selectedOffer))
    }

    return renderItems.concat(
      offers.map((offer) => {
        if (selectedOffer?.id === offer.id) {
          return null
        }
        return renderCard(offer)
      })
    )
  }

  return <Box>{renderResult()}</Box>
}

export const SearchOffersPackages = () => {
  const { accountName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const searchResult = Hooks.useClientSearchPackageOffers(accountName)
  const species = Hooks.useClientLocationSpecies(accountName, true, {
    enabled: !bookingCtx.state.config?.species?.length,
  })
  const userProfile = Hooks.useClientLocationUserProfile(
    accountName,
    // enable only if user data is not set
    bookingCtx.state.cart.contact.email ? '' : sessionCtx.state.user?.lupId || ''
  )

  useEffect(() => {
    if (searchResult.isError) {
      captureError(searchResult.error as Error)
    }
  }, [searchResult.error, searchResult.isError])

  useEffect(() => {
    if (species.data?.results?.length) {
      bookingCtx.dispatch({
        type: 'SET_SPECIES_CONFIG',
        payload: { value: species.data?.results },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species.data])

  useEffect(() => {
    if (userProfile.data) {
      bookingCtx.dispatch({
        type: 'SET_PAYMENT_CONTACT',
        payload: {
          value: userProfile.data,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.data])

  useEffect(() => {
    bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResults' } })
    bookingCtx.dispatch({ type: 'SET_PACKAGES_CONFIG' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {t('searchResults.title.packages')}
      </Typography>
      <Spacer height={3} />
      {searchResult.isLoading ? (
        <SearchResultsSkeleton />
      ) : (
        <SearchPackageResults offers={searchResult.data?.results || []} />
      )}
    </Box>
  )
}
